import { File, Paths } from 'expo-file-system';
import * as LegacyFs from 'expo-file-system/legacy';
import * as IntentLauncher from 'expo-intent-launcher';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { Platform } from 'react-native';

import type {
  ContactRow,
  DocumentRow,
  InventoryItemRow,
  NoteRow,
  PropertyRow,
  QuoteRow,
  SystemRow,
} from '@/db/types';
import { QUOTE_STATUS_META } from '@/lib/quotes';
import type { ServiceHistoryEntry } from '@/lib/tasks';

/**
 * Generate a multi-page "Home Record" PDF — the binder a homeowner can hand
 * to an insurance adjuster, a buyer's agent, or a refinancing lender. v2:
 * proper cover page, table of contents, page-breaks per section, running
 * header + footer.
 *
 * Platform notes:
 * - Native (iOS / Android): expo-print.printToFileAsync renders the HTML to
 *   a real PDF file; we share it via expo-sharing.
 * - Web: expo-print uses window.print() of the CURRENT page on most
 *   browsers — which would just dump the visible Settings screen. We
 *   work around that by writing the HTML to a Blob URL, opening it in a
 *   new tab, and triggering the browser's print dialog there. The user
 *   uses "Save as PDF" to get a file.
 */
export interface BinderInput {
  property: PropertyRow;
  systems: SystemRow[];
  contacts: ContactRow[];
  documents: DocumentRow[];
  history: ServiceHistoryEntry[];
  inventory: InventoryItemRow[];
  /** Wave 35+: free-form notes captured against this home. Rendered
   *  as a "Notes" section near the end of the binder so the PDF is a
   *  true Home Record (the user can search by Ctrl+F in the PDF too). */
  notes?: NoteRow[];
  /** Wave 46+: contractor quotes & estimates. Price history is exactly
   *  the kind of record a buyer, adjuster, or the homeowner-in-3-years
   *  wants in the binder. */
  quotes?: QuoteRow[];
}

// Label maps live in src/lib/property-labels.ts so the home page,
// sidebar, edit form, and PDF all show the same human strings instead
// of raw DB codes (pre60 / dis / q).
import {
  BASEMENT_LABEL,
  EXTERIOR_LABEL,
  FOUNDATION_LABEL,
  GARAGE_LABEL,
  HOME_TYPE_LABEL,
  HVAC_LABEL,
  ROOF_LABEL,
  SEWER_LABEL,
  WATER_LABEL,
  YEAR_LABEL,
} from '@/lib/property-labels';
// System data registry — gives us human labels for s.kind (e.g.
// 'dishwasher' instead of 'dis') and s.subtype ('Furnace' instead of
// 'fur').
import { getSubtypeMeta, getSystemMeta } from '@/data/systems';

/**
 * Human-friendly system name for the PDF — nickname first, then the
 * subtype label, then the kind label, then the raw kind code as a last
 * resort. Keeps `dis`/`wh`/`hvac` codes out of the printed binder.
 */
function systemDisplayName(s: {
  nickname?: string | null;
  kind: string;
  subtype?: string | null;
}): string {
  const meta = getSystemMeta(s.kind);
  const subtype = getSubtypeMeta(s.kind, s.subtype);
  if (s.nickname?.trim()) {
    return subtype || meta
      ? `${s.nickname.trim()} · ${subtype?.label ?? meta?.label}`
      : s.nickname.trim();
  }
  if (subtype?.label && meta?.label) return `${meta.label} · ${subtype.label}`;
  return meta?.label ?? s.kind;
}

function esc(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function formatDate(ms: number | null | undefined): string {
  if (!ms) return '';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCents(c: number | null | undefined): string {
  if (c == null || c === 0) return '';
  return `$${(c / 100).toLocaleString()}`;
}

/**
 * Pick a display title for a note in the PDF — explicit title first,
 * else the first non-empty line of the body, with HTML/markdown chrome
 * stripped.
 */
function noteTitleForPdf(n: NoteRow): string {
  const explicit = n.title?.trim();
  if (explicit) return explicit;
  // Strip HTML + markdown and take the first non-empty line.
  const stripped = n.body
    .replace(/<[^>]+>/g, '\n')
    .replace(/[*_`>#-]/g, '')
    .replace(/&nbsp;/g, ' ');
  const first = stripped
    .split('\n')
    .map((s) => s.trim())
    .find((s) => s.length > 0);
  return first ? first.slice(0, 120) : 'Note';
}

/**
 * Render a note body for the PDF. Accepts either HTML (Wave 35+) or
 * legacy markdown and returns a small whitelisted HTML subset.
 *
 * We don't want a heavy HTML parser in pdf.ts — we re-implement the
 * minimum transforms inline so this file stays self-contained.
 */
function sanitizeNoteHtmlForPdf(body: string): string {
  // Treat markdown bodies as plain text + a few inline transforms.
  if (!/[<][a-zA-Z/]/.test(body)) {
    return body
      .split(/\n{2,}/)
      .map((para) => {
        const trimmed = para.trim();
        if (!trimmed) return '';
        if (/^### /.test(trimmed)) return `<h4>${esc(trimmed.slice(4))}</h4>`;
        if (/^## /.test(trimmed)) return `<h3>${esc(trimmed.slice(3))}</h3>`;
        if (/^# /.test(trimmed)) return `<h2>${esc(trimmed.slice(2))}</h2>`;
        const inline = esc(trimmed)
          .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
          .replace(/\*([^*]+)\*/g, '<i>$1</i>')
          .replace(/_([^_]+)_/g, '<i>$1</i>')
          .replace(/`([^`]+)`/g, '<code>$1</code>')
          .replace(/\n/g, '<br/>');
        return `<p>${inline}</p>`;
      })
      .join('');
  }
  // HTML body — keep a small whitelist, strip everything else.
  const allowed = new Set([
    'p', 'br', 'h1', 'h2', 'h3', 'h4', 'strong', 'b', 'em', 'i', 'u',
    'ul', 'ol', 'li', 'blockquote', 'span', 'code',
  ]);
  return body.replace(/<(\/?)([a-zA-Z0-9]+)([^>]*)>/g, (_full, slash, tag, rest) => {
    const t = tag.toLowerCase();
    if (!allowed.has(t)) return '';
    if (t === 'span') {
      const styleMatch = /style\s*=\s*"([^"]*)"/i.exec(rest);
      const color = styleMatch ? /color\s*:\s*([^;]+)/i.exec(styleMatch[1])?.[1].trim() : null;
      if (slash) return '</span>';
      return color ? `<span style="color: ${esc(color)}">` : '<span>';
    }
    return `<${slash}${t}>`;
  });
}

/**
 * v3 binder layout: compact, flowing, density-first.
 *
 * v2 used one full `.page` div per section with forced page breaks —
 * so even a near-empty property generated a 9+ page binder (cover, TOC,
 * 7 sections). v3 inverts that: the cover/TOC/property summary share a
 * single intro page, each subsequent section is a flowing `<section>`
 * (let the printer paginate naturally), empty sections are dropped
 * entirely, and systems use the same dense-table format inventory does.
 *
 * Typical output for a real home: ~3-5 pages instead of ~10.
 */
export function buildBinderHtml(input: BinderInput): string {
  const { property, systems, contacts, documents, history, inventory, notes = [], quotes = [] } = input;
  const today = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  // Header title precedence: home name > street address > generic
  // "Home Record." The PDF is about the *home*, so owner name is wrong as
  // a primary title — and embedding owner name in a document the user is
  // likely to share with insurance or contractors is a small privacy gain
  // too. Owner stays available in the property summary section.
  const ownerName = [property.owner_first_name, property.owner_last_name]
    .filter((s): s is string => !!s?.trim())
    .join(' ')
    .trim();
  const propertyName =
    property.name?.trim()
    || property.address?.trim()
    || 'Home Record';

  const insideSystems = systems.filter((s) => s.location === 'inside');
  const outsideSystems = systems.filter((s) => s.location === 'outside');
  const totalSpend = history.reduce((sum, h) => sum + (h.cost_cents ?? 0), 0);
  const inventoryTotal = inventory.reduce(
    (sum, i) => sum + (i.value_cents ?? i.purchase_cents ?? 0),
    0
  );
  const insuredCount = inventory.filter((i) => i.is_insured === 1).length;

  // Build a compact set of property-summary key/value pairs, dropping any
  // that are missing so the binder doesn't waste a row on "—" entries.
  // Order mirrors the on-screen My Home summary card so users feel the
  // PDF "looks like the app."
  type Kv = { label: string; value: string };
  const summaryRows: Kv[] = [];
  const push = (label: string, value: string | number | null | undefined) => {
    if (value == null) return;
    const v = String(value).trim();
    if (!v) return;
    summaryRows.push({ label, value: v });
  };
  push('ZIP', property.zip);
  push('Home type', property.home_type ? HOME_TYPE_LABEL[property.home_type] : null);
  push('Build year', property.year_built ?? (property.year_band ? YEAR_LABEL[property.year_band] : null));
  push('Size', property.sqft ? `${property.sqft.toLocaleString()} sq ft` : null);
  push('Stories', property.stories);
  push('Bedrooms / bathrooms',
    property.bedrooms != null || property.bathrooms != null
      ? `${property.bedrooms ?? '—'} / ${property.bathrooms ?? '—'}`
      : null);
  push('Basement', property.basement ? BASEMENT_LABEL[property.basement] : null);
  push('Foundation', property.foundation ? FOUNDATION_LABEL[property.foundation] : null);
  push('Roof material', property.roof_material ? ROOF_LABEL[property.roof_material] : null);
  push('Exterior', property.exterior_material ? EXTERIOR_LABEL[property.exterior_material] : null);
  push('Garage', property.garage ? GARAGE_LABEL[property.garage] : null);
  push('HVAC', property.hvac_type ? HVAC_LABEL[property.hvac_type] : null);
  if (property.secondary_hvac_type) {
    push('Secondary HVAC', HVAC_LABEL[property.secondary_hvac_type] ?? null);
  }
  push('Water source', property.water_source ? WATER_LABEL[property.water_source] : null);
  push('Sewer', property.sewer ? SEWER_LABEL[property.sewer] : null);

  // Section descriptor table — drives both the inline TOC and the
  // sections that actually render. Empty sections are filtered out
  // entirely so the binder reflects the home as it is, not a list of
  // headings the user never filled in.
  const sections = [
    { id: 'inside', label: 'Inside systems', count: insideSystems.length },
    { id: 'outside', label: 'Outside systems', count: outsideSystems.length },
    { id: 'inventory', label: 'Inventory', count: inventory.length },
    { id: 'contacts', label: 'Trusted contacts', count: contacts.length },
    { id: 'history', label: 'Service history', count: history.length },
    { id: 'quotes', label: 'Quotes & estimates', count: quotes.length },
    { id: 'documents', label: 'Documents', count: documents.length },
    { id: 'notes', label: 'Notes', count: notes.length },
  ].filter((s) => s.count > 0);

  // Dense-table row builder for systems — shares structure with
  // inventory / history so the visual rhythm of the binder stays
  // consistent. Card layout was killed in v3: cards were 24pt tall per
  // item; a 30-system home easily ate 3 pages just for systems.
  const systemRow = (s: SystemRow) => {
    const meta = [
      s.subtype ? getSubtypeMeta(s.kind, s.subtype)?.label : null,
      s.model ? `Model ${esc(s.model)}` : null,
      s.filter_size ? `Filter ${esc(s.filter_size)}` : null,
      s.installed_year ? `${s.installed_year}` : null,
    ].filter(Boolean).join(' · ');
    return `
      <tr>
        <td><b>${esc(systemDisplayName(s))}</b></td>
        <td>${meta || '<span class="muted">—</span>'}</td>
        <td class="tiny">${s.notes ? esc(s.notes).slice(0, 120) : ''}</td>
      </tr>`;
  };

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${esc(propertyName)} — Home Record</title>
<style>
  @page { size: letter; margin: 0.5in; }
  @media print { .no-print { display: none !important; } }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif;
    color: #2A251F; font-size: 9.5pt; line-height: 1.4;
    background: white;
    padding: 0;
  }
  .doc {
    max-width: 7.5in;
    margin: 0 auto;
  }

  /* On-screen-only toolbar (Print + Close). */
  .toolbar {
    position: sticky; top: 0; z-index: 10;
    background: #2C4738; padding: 10px 16px;
    display: flex; justify-content: space-between; align-items: center;
  }
  .toolbar-title { color: #FBF6EA; font-weight: 700; font-size: 13px; }
  .toolbar-actions { display: flex; gap: 8px; }
  .toolbar-btn {
    background: #E1A33D; color: #3A2A0C; border: none;
    padding: 7px 14px; border-radius: 6px; cursor: pointer;
    font-weight: 700; font-size: 12px;
  }
  .toolbar-btn.secondary { background: rgba(255,255,255,0.15); color: #FBF6EA; }

  /* Compact intro: cover + TOC + property summary share one page. */
  .hero {
    border-bottom: 2pt solid #2C4738;
    padding-bottom: 10pt;
    margin-bottom: 14pt;
  }
  .hero-eyebrow {
    font-size: 8.5pt; letter-spacing: 2pt; text-transform: uppercase;
    color: #C2842A; font-weight: 700;
  }
  .hero-title {
    font-family: Georgia, serif;
    font-size: 26pt; font-weight: 600; line-height: 1.1;
    letter-spacing: -0.5pt; color: #2C4738;
    margin-top: 4pt;
  }
  .hero-tag {
    margin-top: 6pt; font-size: 10pt; color: #776E5F;
  }
  .hero-meta {
    margin-top: 8pt; font-size: 9pt; color: #4A453E;
  }
  .hero-meta b { color: #2A251F; font-weight: 700; }

  /* Three-stat header strip, sits under the hero. Compact — about 0.6"
     tall and uses the page's full width so it doesn't feel like a third
     section. */
  .stat-strip {
    display: flex; gap: 10pt; margin-bottom: 14pt;
  }
  .stat-cell {
    flex: 1; background: #FCF8EF; border: 0.5pt solid #E3D7BF;
    border-radius: 4pt; padding: 8pt 10pt;
  }
  .stat-cell-num {
    font-family: Georgia, serif; font-size: 16pt; font-weight: 600;
    color: #2C4738; letter-spacing: -0.3pt;
  }
  .stat-cell-label {
    color: #A79C88; font-size: 7.5pt; font-weight: 700;
    letter-spacing: 0.5pt; text-transform: uppercase; margin-top: 1pt;
  }

  /* Sections flow naturally — no forced page breaks. Sections only break
     if they don't fit; small sections cluster on the same page. */
  section {
    margin-bottom: 14pt;
    page-break-inside: auto;
  }
  .section-head {
    display: flex; align-items: baseline; justify-content: space-between;
    border-bottom: 1pt solid #2C4738;
    padding-bottom: 4pt; margin-bottom: 6pt;
  }
  .section-title {
    font-family: Georgia, serif; font-size: 13pt; font-weight: 600;
    color: #2C4738; letter-spacing: -0.2pt;
  }
  .section-count {
    color: #A79C88; font-size: 8pt; font-weight: 700;
    letter-spacing: 0.5pt; text-transform: uppercase;
  }

  /* Notes list — each note rendered as a small card with a title strip
     and the rich-text body. Inline formatting (b/i/u/colors/lists) is
     preserved by sanitizeNoteHtmlForPdf so the PDF looks like the
     in-app rendering. */
  .notes-list { display: flex; flex-direction: column; gap: 8pt; }
  .note-card {
    border: 1pt solid #E3D7BF; border-radius: 4pt;
    padding: 8pt 10pt; background: #FCF8EF;
    page-break-inside: avoid;
  }
  .note-head {
    display: flex; justify-content: space-between; align-items: baseline;
    border-bottom: 1pt solid #E3D7BF; padding-bottom: 4pt; margin-bottom: 4pt;
  }
  .note-title { font-family: Georgia, serif; font-size: 11pt; font-weight: 600; color: #2A251F; }
  .note-date { font-size: 8pt; color: #A79C88; font-weight: 600; letter-spacing: 0.3pt; text-transform: uppercase; }
  .note-body { font-size: 9.5pt; line-height: 1.4; color: #2A251F; }
  .note-body p { margin: 0 0 4pt 0; }
  .note-body h2, .note-body h3, .note-body h4 { margin: 4pt 0 2pt 0; font-family: Georgia, serif; }
  .note-body h2 { font-size: 11pt; }
  .note-body h3 { font-size: 10.5pt; }
  .note-body h4 { font-size: 10pt; }
  .note-body ul, .note-body ol { margin: 2pt 0 4pt 18pt; padding: 0; }
  .note-body blockquote { margin: 2pt 0; padding: 2pt 8pt; border-left: 2pt solid #5C7E55; color: #776E5F; font-style: italic; }
  .note-body code { background: #F1E8D8; padding: 0 2pt; border-radius: 2pt; font-family: 'Courier New', monospace; font-size: 9pt; }

  /* Two-column property summary — half the height of a stacked list. */
  table.summary { width: 100%; border-collapse: collapse; }
  table.summary td {
    padding: 3pt 6pt; vertical-align: top; font-size: 9.5pt;
    border-bottom: 0.25pt solid #E3D7BF;
    width: 25%;
  }
  table.summary td.label {
    color: #776E5F; font-size: 7.5pt; font-weight: 700; letter-spacing: 0.6pt;
    text-transform: uppercase; width: 12%;
  }
  table.summary td.value { font-weight: 600; }

  /* Inline TOC — links to anchors. Compact one-liner per section. */
  .toc {
    display: flex; flex-wrap: wrap; gap: 4pt 14pt;
    margin-bottom: 12pt; font-size: 9pt;
  }
  .toc a {
    color: #2C4738; font-weight: 600; text-decoration: none;
  }
  .toc a .toc-count { color: #A79C88; font-weight: 600; margin-left: 2pt; }

  .pill {
    display: inline-block; padding: 1pt 6pt; border-radius: 99pt;
    background: #EAF0EA; color: #2C4738; font-size: 7.5pt;
    font-weight: 700; margin-right: 3pt; letter-spacing: 0.3pt;
  }
  .pill.insured { background: #DCE7E1; }

  .empty {
    color: #A79C88; font-size: 9pt; padding: 6pt 0;
    font-style: italic;
  }

  table.dense {
    width: 100%; border-collapse: collapse;
    font-size: 8.5pt; line-height: 1.35;
  }
  table.dense thead th {
    text-align: left; padding: 3pt 5pt;
    border-bottom: 0.5pt solid #2C4738;
    color: #2A251F; font-size: 7.5pt; font-weight: 700;
    letter-spacing: 0.5pt; text-transform: uppercase;
  }
  table.dense tbody td {
    padding: 3pt 5pt; vertical-align: top;
    border-bottom: 0.25pt solid #E3D7BF;
  }
  table.dense tbody tr:nth-child(even) td { background: #FBF6EA; }
  table.dense .num { text-align: right; font-variant-numeric: tabular-nums; }
  table.dense .muted { color: #776E5F; }
  table.dense .tiny { font-size: 7.5pt; color: #776E5F; }

  .footnote {
    margin-top: 14pt; padding-top: 6pt;
    border-top: 0.5pt solid #E3D7BF;
    color: #A79C88; font-size: 7.5pt; text-align: center;
  }
</style>
</head>
<body>
  <div class="toolbar no-print">
    <div class="toolbar-title">Homestead · ${esc(propertyName)} · Home Record</div>
    <div class="toolbar-actions">
      <button class="toolbar-btn secondary" onclick="window.close()">Close</button>
      <button class="toolbar-btn" onclick="window.print()">Save as PDF / Print</button>
    </div>
  </div>

  <div class="doc">
    <!-- HERO — compact title block + meta (no full-page cover). -->
    <div class="hero">
      <div class="hero-eyebrow">Homestead · Home Record · ${esc(today)}</div>
      <div class="hero-title">${esc(propertyName)}</div>
      <div class="hero-tag">Property summary, systems, contacts, history, inventory, and documents.</div>
      <div class="hero-meta">
        ${ownerName ? `<b>Owner</b> ${esc(ownerName)}` : ''}
        ${ownerName && property.address ? ' &nbsp;·&nbsp; ' : ''}
        ${property.address ? `<b>Address</b> ${esc(property.address)}` : ''}
        ${property.zip ? ` &nbsp;·&nbsp; <b>ZIP</b> ${esc(property.zip)}` : ''}
      </div>
    </div>

    <!-- Headline stats — three at-a-glance numbers, full-width compact. -->
    <div class="stat-strip">
      <div class="stat-cell">
        <div class="stat-cell-num">${systems.length}</div>
        <div class="stat-cell-label">Systems</div>
      </div>
      <div class="stat-cell">
        <div class="stat-cell-num">${inventory.length}</div>
        <div class="stat-cell-label">Inventory</div>
      </div>
      <div class="stat-cell">
        <div class="stat-cell-num">${formatCents(inventoryTotal) || '—'}</div>
        <div class="stat-cell-label">Tracked value</div>
      </div>
    </div>

    ${sections.length > 0 ? `
    <div class="toc">
      ${sections.map((s, i) => `<a href="#sec-${s.id}">${String(i + 1).padStart(2, '0')} ${esc(s.label)}<span class="toc-count">·${s.count}</span></a>`).join('')}
    </div>` : ''}

    <!-- PROPERTY SUMMARY — two-column key/value table; only filled rows shown. -->
    ${summaryRows.length > 0 ? `
    <section>
      <div class="section-head">
        <div class="section-title">Property summary</div>
        <div class="section-count">${summaryRows.length} attributes</div>
      </div>
      <table class="summary">
        ${(() => {
          const rows: string[] = [];
          for (let i = 0; i < summaryRows.length; i += 2) {
            const a = summaryRows[i];
            const b = summaryRows[i + 1];
            rows.push(`
              <tr>
                <td class="label">${esc(a.label)}</td>
                <td class="value">${esc(a.value)}</td>
                ${b ? `<td class="label">${esc(b.label)}</td><td class="value">${esc(b.value)}</td>` : '<td></td><td></td>'}
              </tr>`);
          }
          return rows.join('');
        })()}
      </table>
    </section>` : ''}

    ${insideSystems.length > 0 ? `
    <section id="sec-inside">
      <div class="section-head">
        <div class="section-title">Inside systems</div>
        <div class="section-count">${insideSystems.length}</div>
      </div>
      <table class="dense">
        <thead>
          <tr>
            <th style="width: 32%">System</th>
            <th style="width: 38%">Detail</th>
            <th style="width: 30%">Notes</th>
          </tr>
        </thead>
        <tbody>${insideSystems.map(systemRow).join('')}</tbody>
      </table>
    </section>` : ''}

    ${outsideSystems.length > 0 ? `
    <section id="sec-outside">
      <div class="section-head">
        <div class="section-title">Outside systems</div>
        <div class="section-count">${outsideSystems.length}</div>
      </div>
      <table class="dense">
        <thead>
          <tr>
            <th style="width: 32%">System</th>
            <th style="width: 38%">Detail</th>
            <th style="width: 30%">Notes</th>
          </tr>
        </thead>
        <tbody>${outsideSystems.map(systemRow).join('')}</tbody>
      </table>
    </section>` : ''}

    ${inventory.length > 0 ? `
    <section id="sec-inventory">
      <div class="section-head">
        <div class="section-title">Inventory</div>
        <div class="section-count">${inventory.length} · ${formatCents(inventoryTotal) || '—'} · ${insuredCount} insured</div>
      </div>
      <table class="dense">
        <thead>
          <tr>
            <th style="width: 28%">Item</th>
            <th style="width: 22%">Brand / model</th>
            <th style="width: 14%">Room</th>
            <th style="width: 14%">Purchased</th>
            <th class="num" style="width: 11%">Paid</th>
            <th class="num" style="width: 11%">Value</th>
          </tr>
        </thead>
        <tbody>
          ${inventory.map((i) => `
            <tr>
              <td>
                <b>${esc(i.name)}</b>
                ${i.is_insured === 1 ? ' <span class="pill insured">INS</span>' : ''}
                ${i.serial_number ? `<div class="tiny">S/N ${esc(i.serial_number)}</div>` : ''}
              </td>
              <td>${[i.brand, i.model].filter(Boolean).map((v) => esc(String(v))).join(' / ') || '<span class="muted">—</span>'}</td>
              <td>${esc(i.room ?? '') || '<span class="muted">—</span>'}</td>
              <td>${i.purchase_date ? esc(formatDate(i.purchase_date)) : '<span class="muted">—</span>'}</td>
              <td class="num">${formatCents(i.purchase_cents) || '<span class="muted">—</span>'}</td>
              <td class="num">${formatCents(i.value_cents) || '<span class="muted">—</span>'}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    </section>` : ''}

    ${contacts.length > 0 ? `
    <section id="sec-contacts">
      <div class="section-head">
        <div class="section-title">Trusted contacts</div>
        <div class="section-count">${contacts.length}</div>
      </div>
      <table class="dense">
        <thead>
          <tr>
            <th style="width: 22%">Name</th>
            <th style="width: 14%">Role</th>
            <th style="width: 18%">Company</th>
            <th style="width: 14%">Phone</th>
            <th style="width: 18%">Email</th>
            <th style="width: 14%">Notes</th>
          </tr>
        </thead>
        <tbody>
          ${contacts.map((c) => `
            <tr>
              <td><b>${esc(c.name)}</b></td>
              <td>${esc(c.role ?? '') || '<span class="muted">—</span>'}</td>
              <td>${esc(c.company ?? '') || '<span class="muted">—</span>'}</td>
              <td>${esc(c.phone ?? '') || '<span class="muted">—</span>'}</td>
              <td>${esc(c.email ?? '') || '<span class="muted">—</span>'}</td>
              <td class="tiny">${c.notes ? esc(c.notes).slice(0, 80) : ''}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    </section>` : ''}

    ${history.length > 0 ? `
    <section id="sec-history">
      <div class="section-head">
        <div class="section-title">Service history</div>
        <div class="section-count">${history.length}${totalSpend > 0 ? ` · ${formatCents(totalSpend)} total` : ''}</div>
      </div>
      <table class="dense">
        <thead>
          <tr>
            <th style="width: 14%">Date</th>
            <th style="width: 38%">Task</th>
            <th style="width: 24%">Vendor / contact</th>
            <th class="num" style="width: 12%">Cost</th>
            <th style="width: 12%">Notes</th>
          </tr>
        </thead>
        <tbody>
          ${history.map((h) => `
            <tr>
              <td>${esc(formatDate(h.completed_at))}</td>
              <td><b>${esc(h.task_title)}</b></td>
              <td>${esc(h.contact_name ?? h.vendor ?? '') || '<span class="muted">—</span>'}</td>
              <td class="num">${formatCents(h.cost_cents) || '<span class="muted">—</span>'}</td>
              <td class="tiny">${h.notes ? esc(h.notes).slice(0, 60) : ''}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    </section>` : ''}

    ${quotes.length > 0 ? `
    <section id="sec-quotes">
      <div class="section-head">
        <div class="section-title">Quotes &amp; estimates</div>
        <div class="section-count">${quotes.length}</div>
      </div>
      <table class="dense">
        <thead>
          <tr>
            <th style="width: 13%">Quoted</th>
            <th style="width: 27%">Project</th>
            <th style="width: 24%">Vendor</th>
            <th class="num" style="width: 13%">Amount</th>
            <th style="width: 11%">Status</th>
            <th style="width: 12%">Notes</th>
          </tr>
        </thead>
        <tbody>
          ${quotes.map((q) => `
            <tr>
              <td>${esc(formatDate(q.quoted_on))}</td>
              <td><b>${esc(q.project ?? q.description ?? q.category ?? 'Quote')}</b></td>
              <td>${esc(q.vendor ?? '') || '<span class="muted">—</span>'}</td>
              <td class="num">${formatCents(q.amount_cents) || '<span class="muted">—</span>'}</td>
              <td><span class="pill">${esc(QUOTE_STATUS_META[q.status]?.label ?? q.status)}</span></td>
              <td class="tiny">${q.notes ? esc(q.notes).slice(0, 60) : ''}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    </section>` : ''}

    ${documents.length > 0 ? `
    <section id="sec-documents">
      <div class="section-head">
        <div class="section-title">Documents on file</div>
        <div class="section-count">${documents.length}</div>
      </div>
      <table class="dense">
        <thead>
          <tr>
            <th style="width: 12%">Kind</th>
            <th style="width: 50%">Title</th>
            <th style="width: 22%">Vendor</th>
            <th style="width: 16%">Expires</th>
          </tr>
        </thead>
        <tbody>
          ${documents.map((d) => `
            <tr>
              <td><span class="pill">${esc(d.kind)}</span></td>
              <td><b>${esc(d.title)}</b></td>
              <td>${esc(d.vendor ?? '') || '<span class="muted">—</span>'}</td>
              <td>${d.expires_at ? esc(formatDate(d.expires_at)) : '<span class="muted">—</span>'}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    </section>` : ''}

    ${notes.length > 0 ? `
    <section id="sec-notes">
      <div class="section-head">
        <div class="section-title">Notes</div>
        <div class="section-count">${notes.length}</div>
      </div>
      <div class="notes-list">
        ${notes.map((n) => `
          <div class="note-card">
            <div class="note-head">
              <div class="note-title">${esc(noteTitleForPdf(n))}</div>
              <div class="note-date">${esc(formatDate(n.updated_at))}${n.pinned === 1 ? ' · pinned' : ''}</div>
            </div>
            <div class="note-body">${sanitizeNoteHtmlForPdf(n.body)}</div>
          </div>`).join('')}
      </div>
    </section>` : ''}

    <div class="footnote">
      Generated by Homestead · ${esc(today)} · Not a substitute for a licensed inspection report.
    </div>
  </div>
</body>
</html>`;
}

export interface PdfResult {
  success: boolean;
  fileUri?: string;
  error?: string;
}

export async function exportBinderPdf(input: BinderInput): Promise<PdfResult> {
  const html = buildBinderHtml(input);

  if (Platform.OS === 'web') {
    // Web workaround: expo-print's printToFileAsync on web typically prints
    // the *current* DOM, not arbitrary HTML — that's why the old binder
    // came out as a screenshot of Settings. Instead, drop the HTML into a
    // Blob URL, open it in a new tab, and let the user use the browser's
    // Save-as-PDF (or just print). The new tab has its own toolbar.
    try {
      // eslint-disable-next-line no-undef
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      // eslint-disable-next-line no-undef
      const url = URL.createObjectURL(blob);
      // eslint-disable-next-line no-undef
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Pop-up blocked — fall back to a download trigger.
        // eslint-disable-next-line no-undef
        const a = document.createElement('a');
        a.href = url;
        a.download = `homestead-${new Date().toISOString().slice(0, 10)}.html`;
        // eslint-disable-next-line no-undef
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Don't revoke immediately — the new tab needs the URL.
      // eslint-disable-next-line no-undef
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { success: true, fileUri: url };
    } catch (err) {
      return { success: false, error: String(err) };
    }
  }

  // Native: real PDF rendering. We:
  //  1) Render to cache via expo-print.
  //  2) Copy out of cache to the app's persistent document directory so
  //     the file isn't wiped on the next cache eviction AND so the URI
  //     is FileProvider-safe to share on Android.
  //  3) Try the share sheet. If it bails, we still return success +
  //     fileUri so the caller can register it in Media & Files as a
  //     guaranteed retrieval path.
  let renderUri: string | null = null;
  try {
    const result = await Print.printToFileAsync({ html, base64: false });
    renderUri = result.uri;
  } catch (err) {
    return { success: false, error: `Couldn't render PDF: ${String(err)}` };
  }

  // Stable filename so repeated exports overwrite instead of piling up.
  const stamp = new Date().toISOString().slice(0, 10);
  const stableName = `homestead-binder-${stamp}.pdf`;
  let finalUri = renderUri;
  try {
    const src = new File(renderUri);
    const dest = new File(Paths.document, stableName);
    if (dest.exists) dest.delete();
    src.copy(dest);
    finalUri = dest.uri;
  } catch (err) {
    // Copy failed for some reason; fall back to the cache URI. Share may
    // still work; if not, the toast surfaces the issue.
    console.warn('PDF copy to document dir failed', err);
  }

  // Android: ACTION_VIEW gives the user an "Open with…" picker showing
  // PDF viewers (Drive, Adobe, Files, etc.) which actually OPEN the file.
  // That's what users expect from a "view my PDF" action — the standard
  // ACTION_SEND share sheet only offers apps that accept attachments.
  if (Platform.OS === 'android') {
    try {
      const contentUri = await LegacyFs.getContentUriAsync(finalUri);
      await IntentLauncher.startActivityAsync('android.intent.action.VIEW', {
        data: contentUri,
        type: 'application/pdf',
        // FLAG_GRANT_READ_URI_PERMISSION — required for the picked app to
        // read the content:// URI we just generated.
        flags: 1,
      });
      return { success: true, fileUri: finalUri };
    } catch (err) {
      // No PDF viewer installed, or IntentLauncher not available in this
      // build. Fall through to the share-sheet path below.
      console.warn('PDF VIEW intent failed, falling back to share', err);
    }
  }

  try {
    const canShare = await Sharing.isAvailableAsync();
    if (!canShare) {
      return {
        success: true,
        fileUri: finalUri,
        error: 'PDF saved. No app on this device handles PDF sharing — find it in Media & Files.',
      };
    }
    await Sharing.shareAsync(finalUri, {
      mimeType: 'application/pdf',
      dialogTitle: 'Save your Home Record',
      UTI: 'com.adobe.pdf',
    });
    return { success: true, fileUri: finalUri };
  } catch (err) {
    return {
      success: true,
      fileUri: finalUri,
      error: `PDF saved to Media & Files. Share dialog failed: ${String(err)}`,
    };
  }
}

// ============================================================
// Emergency Card — single-page printable summary
// ============================================================

/**
 * Single-page printable card showing emergency-critical locations + key
 * contacts. The whole point is: it works when the phone is dead. The
 * homeowner tapes it inside the breaker-panel door or laundry-room
 * cabinet, and when a pipe bursts at 2 AM the answer is on paper.
 *
 * Layout (single 8.5 × 11 page):
 *   1. Header — home name + address + today's date
 *   2. Shutoffs — water main, gas main, electrical panel, HVAC disconnect
 *      with thumbnail photos and how-to-operate notes
 *   3. Plumbing locations — sump, drain cleanout, septic, well
 *   4. Critical contacts — plumber, electrician, HVAC, insurance, HOA
 *   5. Footer — "Tape this inside your breaker-panel door"
 *
 * Photos render at small thumbnail size so the page stays printable on
 * letter without spillover. Notes wrap to 2–3 lines max. Missing
 * entries render as "—" placeholders so the printed sheet is
 * predictable regardless of how much the user filled in.
 */
export interface EmergencyCardInput {
  property: PropertyRow;
  systems: SystemRow[];
  contacts: ContactRow[];
}

/** Display order for the Locations section — must match the catalog
 *  on /emergency-card so users see consistent rows across surfaces. */
const EMERGENCY_KINDS: { kind: string; label: string }[] = [
  { kind: 'water_main_shutoff', label: 'Water main shutoff' },
  { kind: 'gas_shutoff', label: 'Gas main shutoff' },
  { kind: 'panel', label: 'Electrical panel' },
  { kind: 'hvac_condensate_line', label: 'HVAC disconnect' },
  { kind: 'sump', label: 'Sump pump' },
  { kind: 'drain_cleanout', label: 'Main drain cleanout' },
  { kind: 'septic', label: 'Septic tank lid' },
  { kind: 'wellw', label: 'Well cap' },
];

/** Roles surfaced in the contacts grid. Pulled from the contacts table
 *  by matching the contact's `role` field. */
const EMERGENCY_ROLES: { role: string; label: string }[] = [
  { role: 'plumber', label: 'Plumber' },
  { role: 'electrician', label: 'Electrician' },
  { role: 'hvac', label: 'HVAC' },
  { role: 'insurance', label: 'Insurance' },
  { role: 'hoa', label: 'HOA' },
];

export function buildEmergencyCardHtml(input: EmergencyCardInput): string {
  const { property, systems, contacts } = input;
  const today = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const homeName = property.name?.trim() || property.address?.trim() || 'Home';
  const address = property.address?.trim() ?? '';
  const zip = property.zip ?? '';
  const subline = [address, zip].filter(Boolean).join(' · ');

  const findSystem = (kind: string) =>
    systems.find((s) => s.kind === kind && !s.deleted_at) ?? null;
  const findContact = (role: string) =>
    contacts.find(
      (c) =>
        c.role && c.role.toLowerCase() === role && !c.deleted_at
    ) ?? null;

  // Shutoff rows — first four kinds get the larger photo slot. The
  // remaining four go in a more compact 2-col grid below.
  const shutoffs = EMERGENCY_KINDS.slice(0, 4).map(({ kind, label }) => {
    const s = findSystem(kind);
    const photo = s?.location_photo_uri ?? null;
    const where = s?.location_where ?? '';
    const note = s?.location_note ?? '';
    const hasAny = !!(photo || where || note);
    return `
      <div class="loc">
        <div class="loc-photo">${
          photo ? `<img src="${esc(photo)}" alt="" />` : `<div class="loc-empty">No photo</div>`
        }</div>
        <div class="loc-body">
          <div class="loc-label">${esc(label)}</div>
          ${where ? `<div class="loc-where"><span class="loc-field">Where:</span> ${esc(where)}</div>` : ''}
          ${note ? `<div class="loc-note"><span class="loc-field">How:</span> ${esc(note)}</div>` : ''}
          ${!hasAny ? `<div class="loc-note loc-empty-text">—</div>` : ''}
        </div>
      </div>`;
  }).join('');

  const otherLocs = EMERGENCY_KINDS.slice(4).map(({ kind, label }) => {
    const s = findSystem(kind);
    const where = s?.location_where ?? '';
    const note = s?.location_note ?? '';
    const hasAny = !!(where || note);
    return `
      <div class="loc-mini">
        <div class="loc-mini-label">${esc(label)}</div>
        ${where ? `<div class="loc-mini-note"><span class="loc-field">Where:</span> ${esc(where)}</div>` : ''}
        ${note ? `<div class="loc-mini-note"><span class="loc-field">How:</span> ${esc(note)}</div>` : ''}
        ${!hasAny ? `<div class="loc-mini-note loc-empty-text">—</div>` : ''}
      </div>`;
  }).join('');

  const contactRows = EMERGENCY_ROLES.map(({ role, label }) => {
    const c = findContact(role);
    const name = c?.name ?? '';
    const phone = c?.phone ?? '';
    return `
      <tr>
        <td class="role">${esc(label)}</td>
        <td class="name">${esc(name) || '—'}</td>
        <td class="phone">${esc(phone) || '—'}</td>
      </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Emergency Card — ${esc(homeName)}</title>
<style>
  @page { size: letter; margin: 0.5in; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
    color: #2A2421;
    margin: 0;
    background: #FBF6EA;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .page {
    width: 7.5in;
    margin: 0 auto;
    padding: 0;
  }
  .header {
    border-bottom: 3pt solid #2D4E36;
    padding-bottom: 8pt;
    margin-bottom: 14pt;
  }
  .header h1 {
    font-size: 22pt;
    color: #2D4E36;
    margin: 0;
    letter-spacing: -0.5pt;
  }
  .header .sub { font-size: 10pt; color: #776E5F; margin-top: 4pt; }
  .header .date { font-size: 9pt; color: #A79C88; margin-top: 4pt; }

  .section-title {
    font-size: 11pt;
    font-weight: 700;
    color: #2D4E36;
    letter-spacing: 1pt;
    text-transform: uppercase;
    margin: 18pt 0 8pt 0;
    border-bottom: 1pt solid #DCD6C5;
    padding-bottom: 4pt;
  }

  .locs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10pt;
  }
  .loc {
    display: flex;
    gap: 10pt;
    border: 1pt solid #DCD6C5;
    border-radius: 6pt;
    padding: 8pt;
    background: #FFFFFF;
    page-break-inside: avoid;
  }
  .loc-photo { flex: 0 0 80pt; height: 80pt; border-radius: 4pt; overflow: hidden; background: #EDE3D0; }
  .loc-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .loc-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 8pt;
    color: #A79C88;
  }
  .loc-body { flex: 1; min-width: 0; }
  .loc-label { font-size: 10pt; font-weight: 700; color: #2A2421; }
  .loc-where { font-size: 8.5pt; color: #2A2421; margin-top: 4pt; line-height: 1.4; }
  .loc-note { font-size: 8.5pt; color: #4A453E; margin-top: 2pt; line-height: 1.4; }
  .loc-field { font-weight: 700; color: #2D4E36; }
  .loc-empty-text { color: #A79C88; font-style: italic; }

  .loc-grid-mini {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6pt;
  }
  .loc-mini {
    border: 1pt solid #DCD6C5;
    border-radius: 4pt;
    padding: 6pt 8pt;
    background: #FFFFFF;
    page-break-inside: avoid;
  }
  .loc-mini-label { font-size: 9pt; font-weight: 700; color: #2A2421; }
  .loc-mini-note { font-size: 8pt; color: #4A453E; margin-top: 2pt; line-height: 1.3; }

  table.contacts { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
  table.contacts td {
    border-bottom: 1pt solid #EDE3D0;
    padding: 6pt 4pt;
  }
  table.contacts td.role { font-weight: 700; color: #2D4E36; width: 22%; }
  table.contacts td.name { color: #2A2421; width: 38%; }
  table.contacts td.phone { color: #4A453E; width: 40%; }

  .footer {
    margin-top: 18pt;
    padding-top: 8pt;
    border-top: 1pt solid #DCD6C5;
    text-align: center;
    font-size: 8pt;
    color: #A79C88;
    font-style: italic;
  }
</style>
</head>
<body>
  <div class="page">
    <div class="header">
      <h1>${esc(homeName)} — Emergency Card</h1>
      ${subline ? `<div class="sub">${esc(subline)}</div>` : ''}
      <div class="date">Printed ${esc(today)}</div>
    </div>

    <div class="section-title">Shutoffs &amp; critical locations</div>
    <div class="locs">${shutoffs}</div>

    <div class="section-title">Other locations</div>
    <div class="loc-grid-mini">${otherLocs}</div>

    <div class="section-title">Critical contacts</div>
    <table class="contacts">
      <tbody>${contactRows}</tbody>
    </table>

    <div class="footer">
      Tape this inside your breaker-panel door or the laundry-room cabinet.
      Reprint annually — phone numbers churn.
    </div>
  </div>
</body>
</html>`;
}

export async function exportEmergencyCardPdf(
  input: EmergencyCardInput
): Promise<PdfResult> {
  const html = buildEmergencyCardHtml(input);

  if (Platform.OS === 'web') {
    try {
      // eslint-disable-next-line no-undef
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      // eslint-disable-next-line no-undef
      const url = URL.createObjectURL(blob);
      // eslint-disable-next-line no-undef
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // eslint-disable-next-line no-undef
        const a = document.createElement('a');
        a.href = url;
        a.download = `homestead-emergency-card-${new Date().toISOString().slice(0, 10)}.html`;
        // eslint-disable-next-line no-undef
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // eslint-disable-next-line no-undef
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { success: true, fileUri: url };
    } catch (err) {
      return { success: false, error: String(err) };
    }
  }

  let renderUri: string | null = null;
  try {
    const result = await Print.printToFileAsync({ html, base64: false });
    renderUri = result.uri;
  } catch (err) {
    return { success: false, error: `Couldn't render PDF: ${String(err)}` };
  }

  const stamp = new Date().toISOString().slice(0, 10);
  const stableName = `homestead-emergency-card-${stamp}.pdf`;
  let finalUri = renderUri;
  try {
    const src = new File(renderUri);
    const dest = new File(Paths.document, stableName);
    if (dest.exists) dest.delete();
    src.copy(dest);
    finalUri = dest.uri;
  } catch (err) {
    console.warn('Emergency Card copy to document dir failed', err);
  }

  if (Platform.OS === 'android') {
    try {
      const contentUri = await LegacyFs.getContentUriAsync(finalUri);
      await IntentLauncher.startActivityAsync('android.intent.action.VIEW', {
        data: contentUri,
        type: 'application/pdf',
        flags: 1,
      });
      return { success: true, fileUri: finalUri };
    } catch (err) {
      console.warn('Emergency Card VIEW intent failed, falling back to share', err);
    }
  }

  try {
    const canShare = await Sharing.isAvailableAsync();
    if (!canShare) {
      return {
        success: true,
        fileUri: finalUri,
        error: 'Emergency Card saved. No app on this device handles PDF sharing — find it in Documents.',
      };
    }
    await Sharing.shareAsync(finalUri, {
      mimeType: 'application/pdf',
      dialogTitle: 'Save your Emergency Card',
      UTI: 'com.adobe.pdf',
    });
    return { success: true, fileUri: finalUri };
  } catch (err) {
    return {
      success: true,
      fileUri: finalUri,
      error: `Card saved to Documents. Share dialog failed: ${String(err)}`,
    };
  }
}

// ============================================================================
// Annual Home Tasks — printable Jan-Dec checklist
// ============================================================================

/**
 * The annual checklist is the third "important document" Homestead emits.
 * It prints out the user's whole task plan organized by month, with a
 * checkbox next to each row so users can tick them off as they go (think
 * fridge-magnet maintenance list). Designed for people who like a paper
 * artifact next to their bills, or who hand part of the year's maintenance
 * to a service who needs a heads-up of what's coming.
 */
export interface AnnualTasksInput {
  property: PropertyRow;
  /** Active (not yet completed) tasks for this property. Past-due rows
   *  are bucketed into the "Overdue" header at the top so they don't
   *  vanish from the printable just because their date passed. */
  tasks: { id: string; title: string; due_at: number | null; time_minutes: number | null }[];
}

interface MonthBucket {
  monthIndex: number; // 0–11
  year: number;
  tasks: AnnualTasksInput['tasks'];
}

/**
 * Bucket every task into a strict Jan–Dec calendar year (current year).
 * Tasks scheduled for next year are folded into December so the
 * printable stays a clean 1-page-per-month rhythm — users print this
 * once a year as their fridge artifact, they don't want a 13-month
 * "rolling window" rendering.
 *
 * "Overdue" still gets its own block at the top: tasks whose due date
 * is before today (regardless of year) shouldn't quietly vanish from
 * the print just because the month already passed.
 */
function bucketTasksByMonth(
  tasks: AnnualTasksInput['tasks']
): { months: MonthBucket[]; year: number } {
  // The Annual Home Checklist is a printable Jan-Dec reference, not a
  // live "what's late" status report. We deliberately don't carve out
  // an "Overdue" bucket — every task lands in its natural calendar
  // month so January through December each have content the user can
  // tick off on paper as the year unfolds. The upstream caller
  // (getAnnualChecklistTasks) already produces a pure synthetic
  // projection where each rule lives in its season-anchored month for
  // the requested year, regardless of when the user onboarded.
  const now = new Date();
  const year = now.getFullYear();
  const yearStart = new Date(year, 0, 1).getTime();
  const yearEnd = new Date(year + 1, 0, 1).getTime();

  const months: MonthBucket[] = [];
  for (let i = 0; i < 12; i++) {
    months.push({ monthIndex: i, year, tasks: [] });
  }
  for (const t of tasks) {
    if (t.due_at == null) continue;
    // Strays outside the calendar year collapse into the nearest edge
    // month rather than getting dropped — keeps the printable checklist
    // self-contained when a rule fires Dec 31 or Jan 1.
    if (t.due_at >= yearEnd) {
      months[11].tasks.push(t);
      continue;
    }
    if (t.due_at < yearStart) {
      months[0].tasks.push(t);
      continue;
    }
    const d = new Date(t.due_at);
    months[d.getMonth()].tasks.push(t);
  }
  for (const m of months) {
    m.tasks.sort((a, b) => (a.due_at ?? 0) - (b.due_at ?? 0));
  }
  return { months, year };
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function buildAnnualTasksHtml(input: AnnualTasksInput): string {
  const { property, tasks } = input;
  const today = new Date().toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const homeName = property.name?.trim() || property.address?.trim() || 'Home';
  const { months, year } = bucketTasksByMonth(tasks);

  // Single task row used by both overdue and per-month lists.
  // The row is a tight 3-cell flex: checkbox + title + tiny date.
  // No system-label noise — the user is going to read this off a
  // fridge, not a screen. The date shows day-of-month only (the
  // month header above already establishes the month).
  const taskRow = (t: AnnualTasksInput['tasks'][number]) => {
    const due = t.due_at ? new Date(t.due_at) : null;
    const dayLabel = due ? `${due.getDate()}` : '';
    return `
      <li class="row">
        <span class="box"></span>
        <span class="title">${esc(t.title)}</span>
        ${dayLabel ? `<span class="day">${dayLabel}</span>` : ''}
      </li>`;
  };

  // Each month gets a full-width header band followed by a 3-column
  // task list. The list itself uses CSS `column-count: 3` so tasks
  // flow top-to-bottom within each column (newspaper-style). Empty
  // months print "Nothing scheduled" so the rhythm stays consistent.
  // page-break-inside on the wrapping section keeps a month header
  // from being orphaned at the bottom of a page.
  const monthBlocks = months.map((m) => `
    <section class="month">
      <h2 class="month-head">
        <span class="month-name">${esc(MONTH_NAMES[m.monthIndex])}</span>
        <span class="month-count">${m.tasks.length} task${m.tasks.length === 1 ? '' : 's'}</span>
      </h2>
      ${m.tasks.length === 0
        ? '<div class="empty">Nothing scheduled this month.</div>'
        : `<ul class="cols">${m.tasks.map(taskRow).join('')}</ul>`}
    </section>`).join('');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${esc(homeName)} — ${year} Annual Home Tasks</title>
<style>
  /* Letter size with 0.5in margins on every edge. Designed to print
     cleanly on standard US-letter paper without scaling. */
  @page { size: letter; margin: 0.5in; }
  @media print { .no-print { display: none !important; } }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif;
    color: #2A251F; font-size: 9pt; line-height: 1.35;
    background: white;
  }
  .doc { max-width: 7.5in; margin: 0 auto; }

  /* On-screen-only toolbar (Print + Close). Hidden by @media print. */
  .toolbar {
    position: sticky; top: 0; z-index: 10;
    background: #2C4738; padding: 10px 16px;
    display: flex; justify-content: space-between; align-items: center;
  }
  .toolbar-title { color: #FBF6EA; font-weight: 700; font-size: 13px; }
  .toolbar-actions { display: flex; gap: 8px; }
  .toolbar-btn {
    background: #E1A33D; color: #3A2A0C; border: none;
    padding: 7px 14px; border-radius: 6px; cursor: pointer;
    font-weight: 700; font-size: 12px;
  }
  .toolbar-btn.secondary { background: rgba(255,255,255,0.15); color: #FBF6EA; }

  /* Compact intro band. Title + year + one-line subtitle. Doesn't
     try to fill its own page — the months start right under it so
     a typical home prints in 4-6 pages instead of 13. */
  .hero {
    border-bottom: 2pt solid #2C4738;
    padding-bottom: 8pt; margin-bottom: 12pt;
  }
  .hero-eyebrow {
    font-size: 8.5pt; letter-spacing: 2pt; text-transform: uppercase;
    color: #C2842A; font-weight: 700;
  }
  .hero-title {
    font-family: Georgia, serif;
    font-size: 22pt; font-weight: 600; line-height: 1.1;
    letter-spacing: -0.5pt; color: #2C4738;
    margin-top: 4pt;
  }
  .hero-year {
    font-family: Georgia, serif;
    font-size: 14pt; color: #C2842A; font-weight: 600;
    margin-left: 6pt;
  }
  .hero-sub {
    margin-top: 6pt; font-size: 10pt; color: #776E5F;
  }

  /* Overdue: clay-tinted callout at the top. Sits OUTSIDE the
     month grid so users see the backlog without paging through. */
  section.overdue {
    margin-bottom: 14pt;
    background: rgba(176,95,60,0.08);
    border: 0.5pt solid rgba(176,95,60,0.35);
    border-radius: 4pt;
    padding: 10pt 12pt;
  }
  section.overdue h2 {
    font-family: Georgia, serif;
    font-size: 12pt; color: #B05F3C;
    margin-bottom: 6pt;
  }
  section.overdue ul {
    list-style: none;
    column-count: 2;
    column-gap: 18pt;
  }

  /* Each month: full-width header band then a 3-column task flow.
     page-break-inside avoids orphans where the header would otherwise
     land at the bottom of a page with the tasks on the next. */
  section.month {
    page-break-inside: avoid;
    break-inside: avoid;
    margin-bottom: 14pt;
  }
  .month-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    border-bottom: 1.5pt solid #2C4738;
    padding-bottom: 4pt;
    margin-bottom: 6pt;
  }
  .month-name {
    font-family: Georgia, serif;
    font-size: 16pt; font-weight: 600; color: #2C4738;
    letter-spacing: -0.3pt;
  }
  .month-count {
    font-size: 8.5pt; color: #A79C88; font-weight: 700;
    letter-spacing: 0.5pt; text-transform: uppercase;
  }
  .empty {
    font-size: 9pt; color: #A79C88; font-style: italic;
    padding: 4pt 0;
  }

  /* The 3-column task list lives below the month header. Newspaper
     flow (column-count) means rows fill the leftmost column first,
     then the middle, then the right — predictable read order for a
     printable checklist. */
  ul.cols {
    list-style: none;
    column-count: 3;
    column-gap: 18pt;
  }
  .row {
    display: flex; align-items: flex-start; gap: 6pt;
    padding: 3pt 0;
    /* break-inside avoid keeps a row from being sliced across a
       column boundary (checkbox in col 1, title in col 2). */
    break-inside: avoid;
    page-break-inside: avoid;
    border-bottom: 0.25pt dotted #E3D7BF;
  }
  .box {
    width: 9pt; height: 9pt; border: 0.75pt solid #2C4738;
    border-radius: 2pt; flex-shrink: 0; margin-top: 1.5pt;
  }
  .title {
    flex: 1; font-size: 9pt; color: #2A251F; line-height: 1.3;
  }
  .day {
    font-size: 8pt; color: #776E5F; font-variant-numeric: tabular-nums;
    white-space: nowrap; padding-left: 4pt; align-self: flex-start;
    margin-top: 1pt;
  }

  .footnote {
    margin-top: 14pt; padding-top: 6pt;
    border-top: 0.5pt solid #E3D7BF;
    color: #A79C88; font-size: 7.5pt; text-align: center;
  }
</style>
</head>
<body>
  <div class="toolbar no-print">
    <div class="toolbar-title">Homestead · ${esc(homeName)} · ${year} Annual Tasks</div>
    <div class="toolbar-actions">
      <button class="toolbar-btn secondary" onclick="window.close()">Close</button>
      <button class="toolbar-btn" onclick="window.print()">Save as PDF / Print</button>
    </div>
  </div>

  <div class="doc">
    <div class="hero">
      <div class="hero-eyebrow">Homestead · Annual Home Tasks · Generated ${esc(today)}</div>
      <div>
        <span class="hero-title">${esc(homeName)}</span>
        <span class="hero-year">${year}</span>
      </div>
      <div class="hero-sub">Every scheduled task for ${year}, organized by month. Tick the boxes as you go.</div>
    </div>

    ${/* Overdue section removed — the Annual Checklist is a Jan-Dec
         printable reference, not a "what's late" status report. Tasks
         that would have read as overdue now sit in their natural
         calendar month so January-April aren't empty for users who
         onboarded mid-year. */ ''}
    ${monthBlocks}

    <div class="footnote">
      Generated by Homestead · ${esc(today)} · Reprint when your plan changes.
    </div>
  </div>
</body>
</html>`;
}

/** Export the annual checklist PDF. Same web + native flow as the
 *  binder / emergency card exports (Blob URL → new tab on web;
 *  printToFile → ACTION_VIEW or share on native). */
export async function exportAnnualTasksPdf(
  input: AnnualTasksInput
): Promise<PdfResult> {
  const html = buildAnnualTasksHtml(input);

  if (Platform.OS === 'web') {
    try {
      // eslint-disable-next-line no-undef
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      // eslint-disable-next-line no-undef
      const url = URL.createObjectURL(blob);
      // eslint-disable-next-line no-undef
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // eslint-disable-next-line no-undef
        const a = document.createElement('a');
        a.href = url;
        a.download = `homestead-annual-tasks-${new Date().toISOString().slice(0, 10)}.html`;
        // eslint-disable-next-line no-undef
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // eslint-disable-next-line no-undef
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { success: true, fileUri: url };
    } catch (err) {
      return { success: false, error: String(err) };
    }
  }

  let renderUri: string | null = null;
  try {
    const result = await Print.printToFileAsync({ html, base64: false });
    renderUri = result.uri;
  } catch (err) {
    return { success: false, error: `Couldn't render PDF: ${String(err)}` };
  }

  const stamp = new Date().toISOString().slice(0, 10);
  const stableName = `homestead-annual-tasks-${stamp}.pdf`;
  let finalUri = renderUri;
  try {
    const src = new File(renderUri);
    const dest = new File(Paths.document, stableName);
    if (dest.exists) dest.delete();
    src.copy(dest);
    finalUri = dest.uri;
  } catch (err) {
    console.warn('Annual tasks copy to document dir failed', err);
  }

  if (Platform.OS === 'android') {
    try {
      const contentUri = await LegacyFs.getContentUriAsync(finalUri);
      await IntentLauncher.startActivityAsync('android.intent.action.VIEW', {
        data: contentUri,
        type: 'application/pdf',
        flags: 1,
      });
      return { success: true, fileUri: finalUri };
    } catch (err) {
      console.warn('Annual tasks VIEW intent failed, falling back to share', err);
    }
  }

  try {
    const canShare = await Sharing.isAvailableAsync();
    if (!canShare) {
      return {
        success: true,
        fileUri: finalUri,
        error: 'Annual tasks saved. No app on this device handles PDF sharing — find it in Documents.',
      };
    }
    await Sharing.shareAsync(finalUri, {
      mimeType: 'application/pdf',
      dialogTitle: 'Save your Annual Home Tasks',
      UTI: 'com.adobe.pdf',
    });
    return { success: true, fileUri: finalUri };
  } catch (err) {
    return {
      success: true,
      fileUri: finalUri,
      error: `Saved to Documents. Share dialog failed: ${String(err)}`,
    };
  }
}
